if (!customElements.get('composition-placement-filter')) {
  customElements.define('composition-placement-filter', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.container = this.closest('[data-composition-grid]');
      this.grid = this.container.querySelector('#product-grid');
      this.buttons = [...this.querySelectorAll('[data-placement]')];
      this.field = this.querySelector('input');
      this.status = this.container.querySelector('[data-composition-filter-status]');
      this.allLoaded = Number(this.container.dataset.totalPages) <= 1;
      this.hidden = false;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-placement]');
        if (!button || button.getAttribute('aria-disabled') === 'true') return;
        this.select(button.dataset.placement, true);
      });
      this.onPopState = () => this.select(this.placementFromURL());
      window.addEventListener('popstate', this.onPopState);
      this.select(this.placementFromURL());
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      this.controller?.abort();
      this.initialized = false;
    }

    placementFromURL() {
      const placement = new URL(location.href).searchParams.get('composition') || '';
      return this.buttons.some(button => button.dataset.placement === placement) ? placement : '';
    }

    async loadAllCards() {
      if (this.allLoaded) return;
      this.controller = new AbortController();
      const pages = Number(this.container.dataset.totalPages);
      const current = Number(this.container.dataset.currentPage);
      const cardsByPage = new Map([[current, [...this.grid.querySelectorAll('[data-composition-card]')]]]);
      // Preserve Shopify sorting and native filters across every result page.
      for (let page = 1; page <= pages; page++) {
        if (page === current) continue;
        const url = new URL(location.href);
        url.searchParams.set('section_id', this.container.dataset.sectionId);
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Composition page unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = doc.querySelector('[data-composition-grid]');
        if (!source || Number(source.dataset.currentPage) !== page) throw new Error('Invalid composition page');
        cardsByPage.set(page, [...source.querySelectorAll('[data-composition-card]')]);
      }
      const fragment = document.createDocumentFragment();
      const ids = new Set();
      for (let page = 1; page <= pages; page++) {
        for (const card of cardsByPage.get(page)) {
          if (ids.has(card.dataset.compositionCard)) continue;
          ids.add(card.dataset.compositionCard);
          fragment.append(card);
        }
      }
      this.grid.replaceChildren(fragment);
      this.container.querySelectorAll('.pagination-wrapper').forEach(el => { el.hidden = true; });
      this.allLoaded = true;
      window.solsticeCardMedia?.scan(this.grid);
      const color = this.container.querySelector('[data-color-family]:checked')?.dataset.colorFamily;
      if (color) window.dispatchEvent(new CustomEvent('global-color-switch', { detail: { color } }));
    }

    async select(placement, updateURL = false) {
      if (this.loading) return;
      this.loading = true;
      this.setAttribute('aria-busy', 'true');
      try {
        if (placement) await this.loadAllCards();
        const cards = [...this.grid.querySelectorAll('[data-composition-card]')];
        let count = 0;
        for (const card of cards) {
          const matches = !placement || card.dataset.placements.includes(`|${placement}|`);
          card.hidden = !matches;
          if (matches) count++;
        }
        this.grid.querySelectorAll('.signature-grid-span, .promo-grid-span').forEach(el => { el.hidden = Boolean(placement); });
        this.buttons.forEach(button => {
          const value = button.dataset.placement;
          const active = value === placement;
          const unavailable = this.allLoaded && value && !cards.some(card => card.dataset.placements.includes(`|${value}|`));
          button.setAttribute('aria-pressed', String(active));
          button.setAttribute('aria-disabled', String(Boolean(unavailable && !active)));
          button.title = unavailable ? 'Aucune composition pour cet emplacement pour le moment' : '';
        });
        this.field.value = placement;
        this.field.disabled = !placement;
        if (this.allLoaded) {
          this.container.querySelector('.product-count-seo').textContent = `${count} produit${count === 1 ? '' : 's'}`;
          this.container.querySelector('.filter-text-mobile').textContent = `Filtrer et trier (${count})`;
        }
        this.status.hidden = count > 0 || !placement;
        this.status.textContent = this.status.hidden ? '' : 'Aucune composition pour cet emplacement. Découvrez les autres avec « Toutes ».';
        if (updateURL) {
          const url = new URL(location.href);
          if (placement) url.searchParams.set('composition', placement);
          else url.searchParams.delete('composition');
          url.searchParams.delete('page');
          if (url.href !== location.href) history.pushState({}, '', url);
          const active = this.buttons.find(button => button.dataset.placement === placement);
          const options = this.querySelector('.composition-filters__options');
          const left = active.offsetLeft - options.offsetLeft;
          if (left < options.scrollLeft || left + active.offsetWidth > options.scrollLeft + options.clientWidth) {
            options.scrollTo({ left: Math.max(0, left - 8), behavior: 'instant' });
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.status.textContent = 'Le filtre n’a pas pu être chargé. Réessayez en sélectionnant un emplacement.';
          this.status.hidden = false;
        }
      } finally {
        this.loading = false;
        this.removeAttribute('aria-busy');
        this.container.dispatchEvent(new Event('solstice:composition-filtered'));
      }
    }
  });
}
